import type { Simulation, SimulationNodeDatum } from 'd3-force'
import { forceSimulation, forceX, forceY, forceCollide } from 'd3-force'
import type { D3Pie, ProjectFunction } from '../types'

export interface ForceNode extends SimulationNodeDatum {
  id: string
  x: number
  y: number
  radius: number
  originalX: number
  originalY: number
}

export function createPieForceSimulation (
  pies: D3Pie[],
  project: ProjectFunction
): { simulation: Simulation<ForceNode, undefined>, nodes: ForceNode[] } {
  // Convert pies to force nodes with projected coordinates
  const nodes: ForceNode[] = pies.map(pie => {
    const projected = project([pie.coordinates.latitude, pie.coordinates.longitude])
    return {
      id: pie.id,
      x: projected.x,
      y: projected.y,
      radius: pie.radius,
      originalX: projected.x,
      originalY: projected.y
    }
  })

  // Create force simulation
  const simulation = forceSimulation<ForceNode>(nodes)
    // Collision force to prevent overlaps - add padding between pies
    .force('collision', forceCollide<ForceNode>()
      .radius(d => d.radius + 10) // 10px padding between pies
      .strength(1)
      .iterations(3))
    // Attraction force to original positions - acts like a spring
    .force('x', forceX<ForceNode>(d => d.originalX).strength(0.1))
    .force('y', forceY<ForceNode>(d => d.originalY).strength(0.1))
    // Configure simulation
    .alphaDecay(0.02) // Slower cooling for smoother animation
    .velocityDecay(0.3) // More damping for stability

  return { simulation, nodes }
}

export function getNodeById (nodes: ForceNode[], id: string): ForceNode | undefined {
  return nodes.find(node => node.id === id)
}
